import * as React from 'react'
import { cn } from '@/lib/utils'

export interface EmptyStateProps {
  /** Icono de lucide, ya como elemento: `<Inbox size={28} />`. */
  icono?: React.ReactNode
  titulo: string
  /**
   * Qué hacer ahora. NO es un adorno: un vacío que solo dice "No hay nada" deja al asesor
   * sin saber si el sistema falló, si le faltan permisos o si simplemente no ha empezado.
   */
  descripcion?: string
  accion?: React.ReactNode
  /** `error` para cuando el vacío es un fallo y no un principio. */
  tono?: 'neutro' | 'error'
  className?: string
}

/**
 * El estado vacío, con la misma cara en todo el portal.
 *
 * Cada listado tenía el suyo: uno decía "Sin datos", otro dejaba la tabla en blanco, y el
 * de cotizaciones no decía nada. La primera pantalla que ve un asesor nuevo es justamente
 * un vacío, y es la que decide si entiende el producto o cree que está roto.
 */
export function EmptyState({ icono, titulo, descripcion, accion, tono = 'neutro', className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed px-4 py-10 text-center sm:px-6',
        tono === 'error' ? 'border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger/15' : 'border-line bg-surface-muted',
        className
      )}
    >
      {icono && (
        <div
          aria-hidden="true"
          className={cn(
            'mb-3 flex h-12 w-12 items-center justify-center rounded-full',
            tono === 'error' ? 'bg-danger-100 dark:bg-danger/15 text-danger-700 dark:text-danger-300' : 'bg-card text-subtle shadow-sm'
          )}
        >
          {icono}
        </div>
      )}
      <p className={cn('text-body font-medium', tono === 'error' ? 'text-danger-700 dark:text-danger-300' : 'text-foreground')}>
        {titulo}
      </p>
      {descripcion && <p className="mt-1 max-w-md text-caption text-subtle">{descripcion}</p>}
      {accion && <div className="mt-4">{accion}</div>}
    </div>
  )
}

export default EmptyState
